import { useEffect, useState } from 'react';
import { Grid, MenuItem, TextField } from '@material-ui/core';
import { css } from '@emotion/react';
import {
    ResponsiveSidebarDialog,
    ModalTitle,
    ModalContent,
    StickyModalActions,
    SecondaryButton,
    PrimaryButton,
} from '@/styles/blocks';
import AddressAutocomplete from '@/components/misc/AddressAutocomplete';
import { geocodeToLatLng } from '@/utilities/handleGeocode';

const UNITS = [
    { value: 'mi', label: 'mi.' },
    { value: 'km', label: 'km' },
];

const MAX_RADIUS = 100;

const AddressRadiusModal = ({ open, onClose, onConfirm }) => {
    const [address, setAddress] = useState('');
    const [coords, setCoords] = useState(null);
    const [focused, setFocused] = useState(false);
    const [minRadius, setMinRadius] = useState('0');
    const [maxRadius, setMaxRadius] = useState('');
    const [unit, setUnit] = useState('mi');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setAddress('');
            setCoords(null);
            setFocused(false);
            setMinRadius('0');
            setMaxRadius('');
            setUnit('mi');
            setSubmitting(false);
            setError('');
        }
    }, [open]);

    const handleInput = (value) => {
        setAddress(typeof value === 'string' ? value : value?.target?.value || '');
        setCoords(null);
    };

    const handleSelect = async (selected) => {
        setAddress(selected);
        try {
            const latLng = await geocodeToLatLng(selected);
            setCoords({ lat: latLng.lat, lng: latLng.lng });
            setError('');
        } catch (err) {
            setCoords(null);
            setError('Could not locate that address. Try another.');
        }
    };

    const minNumber = Number(minRadius);
    const maxNumber = Number(maxRadius);
    const minInRange =
        minRadius !== '' && !Number.isNaN(minNumber) && minNumber >= 0 && minNumber <= MAX_RADIUS;
    const maxInRange =
        maxRadius !== '' && !Number.isNaN(maxNumber) && maxNumber > 0 && maxNumber <= MAX_RADIUS;
    const bandOk = minInRange && maxInRange && maxNumber > minNumber;
    const canSubmit = !!address && !!coords && bandOk && !submitting;

    const minHelperText = (() => {
        if (minRadius === '') return 'Required';
        if (!minInRange) return `Enter 0–${MAX_RADIUS}`;
        return '';
    })();
    const maxHelperText = (() => {
        if (maxRadius === '') return 'Required';
        if (!maxInRange) return `Enter 0–${MAX_RADIUS}`;
        if (minInRange && maxNumber <= minNumber) return 'Must be greater than min';
        return '';
    })();

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await onConfirm({
                address,
                center: [coords.lng, coords.lat],
                minRadius: minNumber,
                maxRadius: maxNumber,
                unit,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ResponsiveSidebarDialog open={open} onClose={onClose}>
            <ModalTitle title="Add Zip Codes by Address Radius" onClose={onClose} />
            <ModalContent width={460}>
                <Grid
                    container
                    direction="column"
                    css={css`
                        gap: 16px;
                        min-height: 360px;
                    `}
                >
                    <AddressAutocomplete
                        value={address}
                        onInput={handleInput}
                        onSelect={handleSelect}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        open={focused}
                        showLabel
                        label="Address"
                    />
                    <Grid
                        container
                        css={css`
                            gap: 12px;
                            flex-wrap: nowrap;
                        `}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="number"
                            label="Min Radius"
                            value={minRadius}
                            inputProps={{ min: 0, max: MAX_RADIUS, step: 'any' }}
                            error={!!minHelperText}
                            helperText={minHelperText}
                            onChange={(e) => setMinRadius(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            type="number"
                            label="Max Radius"
                            value={maxRadius}
                            inputProps={{ min: 0, max: MAX_RADIUS, step: 'any' }}
                            error={!!maxHelperText}
                            helperText={maxHelperText}
                            onChange={(e) => setMaxRadius(e.target.value)}
                        />
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Unit"
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            css={css`
                                min-width: 100px;
                            `}
                        >
                            {UNITS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {error ? (
                        <div
                            css={css`
                                color: #d23e3e;
                                font-size: 12px;
                            `}
                        >
                            {error}
                        </div>
                    ) : null}
                </Grid>
            </ModalContent>
            <StickyModalActions>
                <Grid item>
                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                </Grid>
                <Grid item>
                    <PrimaryButton disabled={!canSubmit} onClick={submit}>
                        Confirm
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default AddressRadiusModal;
